import { Loader2 } from 'lucide-react';
import { useCallback, useMemo, useState } from 'react';

import {
  AdminSectionCard,
  ConfirmSwitch,
  HelpAnchor,
  InlineError,
  InputField,
  SkeletonCardGrid,
  SkeletonForm,
} from '@/design-system';
import { Commentable } from '@/shared/comments/Commentable';
import { cn } from '@/shared/cn';
import { BUTTON_PRIMARY, FIELD_INPUT } from '@/shared/ui-class';
import { SEED_AI_MODELS } from '@/versions/v1/shared/admin-seed';
import { AdminSceneShell } from './admin-shared';
import {
  CLAUDE_CATALOG_MODELS,
  CLAUDE_MODELS_CONFIG_SLUG,
  DEFAULT_MODEL_ID,
  GOOGLE_CATALOG_MODELS,
  GOOGLE_MODELS_CONFIG_SLUG,
  getModel,
  resolvedFallbackLabel,
  type AllowlistConfigValue,
} from './AiModelsScene.transform';
import { ModelAllowlistPanelBase } from './ModelAllowlistPanelBase';

function AiModelsPanelSkeleton() {
  return (
    <div className="space-y-6" aria-hidden>
      <AdminSectionCard title="Platform default model">
        <SkeletonForm fields={1} />
      </AdminSectionCard>
      <div className="grid gap-6 lg:grid-cols-2">
        <AdminSectionCard title="Anthropic ADK">
          <SkeletonCardGrid count={3} cols={2} />
        </AdminSectionCard>
        <AdminSectionCard title="Google ADK">
          <SkeletonCardGrid count={3} cols={2} />
        </AdminSectionCard>
      </div>
    </div>
  );
}

export function AiModelsScene() {
  const [seed, setSeed] = useState(SEED_AI_MODELS);
  const [loading] = useState(false);

  const [pdValue, setPdValue] = useState<string | null>(seed.platformDefaultModelId);
  const [pdInitial, setPdInitial] = useState<string | null>(seed.platformDefaultModelId);
  const [pdSaving, setPdSaving] = useState(false);
  const [pdSaveError, setPdSaveError] = useState<string | null>(null);

  const [claudeKit, setClaudeKit] = useState(seed.claude);
  const [googleKit, setGoogleKit] = useState(seed.google);
  const [claudeSwitchSaving, setClaudeSwitchSaving] = useState(false);
  const [googleSwitchSaving, setGoogleSwitchSaving] = useState(false);
  const [claudeSwitchError, setClaudeSwitchError] = useState<string | null>(null);
  const [googleSwitchError, setGoogleSwitchError] = useState<string | null>(null);

  const [webSearch, setWebSearch] = useState(seed.webSearch);
  const [webSearchSaving, setWebSearchSaving] = useState(false);
  const [webSearchSaveError, setWebSearchSaveError] = useState<string | null>(null);

  const [maxLlmCalls, setMaxLlmCalls] = useState(
    seed.google.maxLlmCalls ? String(seed.google.maxLlmCalls) : '',
  );
  const [initialMaxLlmCalls, setInitialMaxLlmCalls] = useState(
    seed.google.maxLlmCalls ? String(seed.google.maxLlmCalls) : '',
  );

  const pdDirty = pdValue !== pdInitial;
  const maxLlmDirty = maxLlmCalls !== initialMaxLlmCalls;

  const savePlatformDefault = () => {
    setPdSaving(true);
    setPdSaveError(null);
    setPdValue((v) => v);
    setPdInitial(pdValue);
    setSeed((s) => ({ ...s, platformDefaultModelId: pdValue }));
    setPdSaving(false);
  };

  const saveKitEnabled = (kit: 'claude' | 'google', enabled: boolean) => {
    if (kit === 'claude') {
      setClaudeSwitchSaving(true);
      setClaudeSwitchError(null);
      setClaudeKit((k) => ({ ...k, kitEnabled: enabled }));
      setClaudeSwitchSaving(false);
    } else {
      setGoogleSwitchSaving(true);
      setGoogleSwitchError(null);
      setGoogleKit((k) => ({ ...k, kitEnabled: enabled }));
      setGoogleSwitchSaving(false);
    }
  };

  const saveWebSearch = (kit: 'claude' | 'google', enabled: boolean) => {
    setWebSearchSaving(true);
    setWebSearchSaveError(null);
    setWebSearch((s) => ({
      ...s,
      claude: kit === 'claude' ? enabled : s.claude,
      google: kit === 'google' ? enabled : s.google,
    }));
    setWebSearchSaving(false);
  };

  const onGoogleConfigLoaded = useCallback((value: Record<string, unknown>) => {
    const raw = value.maxLlmCalls;
    const str = typeof raw === 'number' && raw > 0 ? String(raw) : '';
    setMaxLlmCalls(str);
    setInitialMaxLlmCalls(str);
  }, []);

  const buildGoogleSaveValue = useCallback(
    (base: AllowlistConfigValue) => {
      const trimmed = maxLlmCalls.trim();
      const parsed = trimmed === '' ? null : Number.parseInt(trimmed, 10);
      return {
        ...base,
        maxLlmCalls:
          parsed !== null && Number.isFinite(parsed) && parsed > 0 ? parsed : null,
      };
    },
    [maxLlmCalls],
  );

  const googleExtraFields = useMemo(
    () => (
      <AdminSectionCard
        title="Max LLM calls per turn"
        description="Caps tool↔model cycles in the Google ADK agent loop. Leave empty to use the server default (env or 50)."
      >
        <InputField
          id="google-max-llm-calls"
          type="number"
          min={1}
          step={1}
          placeholder="e.g. 50"
          value={maxLlmCalls}
          onChange={(e) => setMaxLlmCalls(e.target.value)}
        />
      </AdminSectionCard>
    ),
    [maxLlmCalls],
  );

  const saveClaudeAllowlist = (value: Record<string, unknown>) => {
    setClaudeKit((k) => ({
      ...k,
      allowedIds: (value.allowedIds as string[] | null) ?? null,
    }));
  };

  const saveGoogleAllowlist = (value: Record<string, unknown>) => {
    setGoogleKit((k) => ({
      ...k,
      allowedIds: (value.allowedIds as string[] | null) ?? null,
      maxLlmCalls: (value.maxLlmCalls as number | null) ?? k.maxLlmCalls,
    }));
    if (typeof value.maxLlmCalls === 'number') {
      setInitialMaxLlmCalls(String(value.maxLlmCalls));
    }
  };

  const panel = loading ? (
    <AiModelsPanelSkeleton />
  ) : (
    <div className="space-y-6">
      <AdminSectionCard
        title="Platform default model"
        description="New users and new chats with no saved preference start on this model. Existing chats always keep the model they last used."
      >
        {pdSaveError ? <InlineError>{pdSaveError}</InlineError> : null}

        <div className="space-y-3">
          <select
            aria-label="Platform default model"
            value={pdValue ?? ''}
            onChange={(e) =>
              setPdValue(e.target.value === '' ? null : e.target.value)
            }
            className={cn(FIELD_INPUT, 'w-full text-zinc-900 dark:text-zinc-100')}
          >
            <option value="">
              No platform default — use the app default (
              {getModel(DEFAULT_MODEL_ID)?.label ?? DEFAULT_MODEL_ID})
            </option>
            {seed.allModels.map((m) => (
              <option key={m.id} value={m.id}>
                {m.label} ({m.id})
              </option>
            ))}
          </select>

          {pdValue === null ? (
            <p className="text-xs text-muted">
              New users, and new chats with no saved preference, start on the app default.
              Existing chats always keep the model they last used.
            </p>
          ) : null}

          {pdSaveError ? <InlineError>{pdSaveError}</InlineError> : null}

          <div className="flex justify-end">
            <button
              type="button"
              disabled={!pdDirty || pdSaving}
              onClick={savePlatformDefault}
              className={BUTTON_PRIMARY}
            >
              {pdSaving ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
              Save
            </button>
          </div>
        </div>
      </AdminSectionCard>

      <div className="grid gap-6 lg:grid-cols-2">
        <AdminSectionCard
          title="Anthropic ADK"
          description="Claude models run on the Anthropic agent SDK. Control which models users can select in the composer."
          actions={
            <ConfirmSwitch
              id="claude-kit-enabled"
              aria-label="Enable Anthropic ADK"
              checked={claudeKit.kitEnabled}
              saving={claudeSwitchSaving}
              guardWhen="off"
              confirm={{
                title: 'Disable the Anthropic ADK?',
                description: (
                  <span>
                    All Claude models are hidden from every user immediately. Chats that last used
                    a Claude model keep their history, but their next message will run on{' '}
                    <strong>
                      {resolvedFallbackLabel(
                        'claude',
                        pdValue,
                        seed.appDefaultModelId,
                      )}
                    </strong>{' '}
                    instead. Your saved model selection for this section is kept and restored when
                    you re-enable.
                  </span>
                ),
                confirmLabel: 'Disable',
                tone: 'danger',
              }}
              onCheckedChange={() => saveKitEnabled('claude', true)}
              onConfirm={() => saveKitEnabled('claude', false)}
            />
          }
        >
          <div
            className={cn(
              'space-y-4',
              !claudeKit.kitEnabled && 'pointer-events-none select-none opacity-50',
            )}
            aria-disabled={!claudeKit.kitEnabled}
          >
            {claudeSwitchError ? <InlineError>{claudeSwitchError}</InlineError> : null}

            <AdminSectionCard
              title="Web search"
              description="Anthropic's first-party web_search tool. Off hides the composer toggle and denies the tool on every Claude model."
              actions={
                <ConfirmSwitch
                  id="claude-web-search"
                  aria-label="Anthropic web search"
                  checked={webSearch.claude}
                  saving={webSearchSaving}
                  guardWhen="off"
                  confirm={{
                    title: 'Turn off web search for the Anthropic ADK?',
                    description: (
                      <span>
                        Users lose the composer web-search toggle and the WebSearch tool on every
                        Claude model.{' '}
                        <strong>Skills that rely on web search will silently degrade</strong> —
                        review the{' '}
                        <a className="underline" href="/admin/predefined-skills">
                          Skills library
                        </a>{' '}
                        and disable any that depend on it.
                      </span>
                    ),
                    confirmLabel: 'Turn off',
                    tone: 'danger',
                  }}
                  onCheckedChange={() => saveWebSearch('claude', true)}
                  onConfirm={() => saveWebSearch('claude', false)}
                />
              }
            />

            {webSearchSaveError ? <InlineError>{webSearchSaveError}</InlineError> : null}

            <ModelAllowlistPanelBase
              kit="claude"
              catalog={CLAUDE_CATALOG_MODELS}
              configSlug={CLAUDE_MODELS_CONFIG_SLUG}
              panelDescription="Select the Claude models available to users. Deselecting all hides every Claude model."
              disabledHint="Anthropic API key not configured — Claude models are hidden regardless of this allowlist."
              denyAllWarning="This hides every Claude model from all users. Users can still run Google models if enabled."
              modelHealth={seed.modelHealth}
              initialAllowedIds={claudeKit.allowedIds}
              onSave={saveClaudeAllowlist}
            />
          </div>
        </AdminSectionCard>

        <AdminSectionCard
          title="Google ADK"
          description="Gemini models run on the Google agent SDK. Control which models users can select and configure the per-turn tool cycle cap."
          actions={
            <ConfirmSwitch
              id="google-kit-enabled"
              aria-label="Enable Google ADK"
              checked={googleKit.kitEnabled}
              saving={googleSwitchSaving}
              guardWhen="off"
              confirm={{
                title: 'Disable the Google ADK?',
                description: (
                  <span>
                    All Gemini models are hidden from every user immediately. Chats that last used
                    a Gemini model keep their history, but their next message will run on{' '}
                    <strong>
                      {resolvedFallbackLabel('google', pdValue, seed.appDefaultModelId)}
                    </strong>{' '}
                    instead. Your saved model selection for this section is kept and restored when
                    you re-enable.
                  </span>
                ),
                confirmLabel: 'Disable',
                tone: 'danger',
              }}
              onCheckedChange={() => saveKitEnabled('google', true)}
              onConfirm={() => saveKitEnabled('google', false)}
            />
          }
        >
          <div
            className={cn(
              'space-y-4',
              !googleKit.kitEnabled && 'pointer-events-none select-none opacity-50',
            )}
            aria-disabled={!googleKit.kitEnabled}
          >
            {googleSwitchError ? <InlineError>{googleSwitchError}</InlineError> : null}

            <AdminSectionCard
              title="Web search"
              description="Gemini Grounding with Google Search. Off hides the composer toggle and disables grounding on every Gemini model."
              actions={
                <ConfirmSwitch
                  id="google-web-search"
                  aria-label="Google web search"
                  checked={webSearch.google}
                  saving={webSearchSaving}
                  guardWhen="off"
                  confirm={{
                    title: 'Turn off web search for the Google ADK?',
                    description: (
                      <span>
                        Users lose the composer web-search toggle and Gemini Grounding on every
                        Google model.{' '}
                        <strong>Skills that rely on web search will silently degrade</strong> —
                        review the{' '}
                        <a className="underline" href="/admin/predefined-skills">
                          Skills library
                        </a>{' '}
                        and disable any that depend on it.
                      </span>
                    ),
                    confirmLabel: 'Turn off',
                    tone: 'danger',
                  }}
                  onCheckedChange={() => saveWebSearch('google', true)}
                  onConfirm={() => saveWebSearch('google', false)}
                />
              }
            />

            <ModelAllowlistPanelBase
              kit="google"
              catalog={GOOGLE_CATALOG_MODELS}
              configSlug={GOOGLE_MODELS_CONFIG_SLUG}
              panelDescription="Select the Gemini models available to users. Deselecting all hides every Gemini model."
              disabledHint="GOOGLE_ADK_ENABLED is off or no Gemini API key — Google models are hidden regardless of this allowlist."
              denyAllWarning="This hides every Google model from all users. Users can still run Claude models if enabled."
              buildSaveValue={buildGoogleSaveValue}
              onConfigLoaded={onGoogleConfigLoaded}
              extraDirty={maxLlmDirty}
              modelHealth={seed.modelHealth}
              initialAllowedIds={googleKit.allowedIds}
              onSave={saveGoogleAllowlist}
            >
              {googleExtraFields}
            </ModelAllowlistPanelBase>
          </div>
        </AdminSectionCard>
      </div>
    </div>
  );

  return (
    <AdminSceneShell
      sceneName="AiModelsScene"
      title="AI models"
      description="Platform default model and per-kit allowlists."
      headerActions={<HelpAnchor doc="workspace/admin" />}
    >
      <Commentable name="Panel">{panel}</Commentable>
    </AdminSceneShell>
  );
}
